import * as yup from 'yup'
import { pick } from 'lodash-es'
import { BUILDING_NUMBER_REGEX } from './constants'
import {
    isPossiblePhoneNumber,
    isValidPhoneNumber,
    validatePhoneNumberLength,
    parsePhoneNumber,
} from 'libphonenumber-js/max'

const isCompanyInvoice = (val: string) => val === 'company'

const fields = {
    firstname: yup.string().required('errors.string.first_name'),
    lastname: yup.string().required('errors.string.last_name'),
    company: yup.string().nullable(),
    vat_id: yup
        .string()
        .nullable()
        .test('vat_id', 'errors.string.tin', (value: any) => {
            if (!value) return true

            return value && value.length === 10 && /^[0-9]+$/.test(value)
        }),
    postcode: yup
        .string()
        .required('errors.string.postcode_required')
        .test('postcode', 'errors.string.postcode', (value: any, schema) => {
            if (schema?.parent?.country_code === 'PL') {
                if (!value || !value.length) return false

                return /^[0-9]{2}-[0-9]{3}$/.test(value)
            }

            return true
        })
        .test('postcode', 'errors.string.postcode_required', (value: any) => {
            if (!value) return false

            return value?.length <= 10
        }),

    city: yup.string().test('city', 'errors.string.city', (value: any) => {
        if (!value) return false

        return /^([^0-9]*)$/.test(value)
    }),
    'street[0]': yup
        .string()
        .test('street', 'errors.string.street', (_, schema) => {
            return schema?.parent?.street?.[0]
        }),
    'street[1]': yup
        .string()
        .test('street', 'errors.string.street_no', (_, schema) => {
            const { isInpost } = schema.options.context || {}
            if (isInpost) return true

            const value = schema?.parent?.street?.[1]
            if (!value) return false

            return BUILDING_NUMBER_REGEX.test(value)
        }),

    'street[2]': yup.string(),
    country_code: yup.string().required('errors.string.country'),
    telephone: yup
        .string()
        .required('errors.string.tel')
        .test('telephone', 'errors.string.tel', (value: any) => {
            if (value === undefined) return false
            const parse = parsePhoneNumber(value)
            const parsedNumber: any = parsePhoneNumber(value)
            if (validatePhoneNumberLength(parsedNumber?.nationalNumber, parse?.country) !== undefined) {
                return false
            }
            else if (!isPossiblePhoneNumber(value.toString(), parse?.country)) {
                return false
            }
            return isValidPhoneNumber(value.toString(), parse?.country)
        }),

    region_id: yup
        .number()
        .nullable()
        .test('region_id', 'errors.string.no_region', (value: any, context: any) => {
            if (!context?.options?.context?.regionsAvailable) return true
            return value
        }),
    region: yup.string().nullable(),
}

export const addressSchema = yup.object().shape({
    email: yup.string().email().required('errors.string.email'),
    shipping_address: yup.object().shape({
        ...fields,
    }),
    billing_address: yup.object().when('receive_invoice', {
        is: true,
        then: yup
            .object({
                ...pick(fields, [
                    'postcode',
                    'city',
                    'street[0]',
                    'street[1]',
                    'street[2]',
                    'country_code',
                ]),
                firstname: yup.string().when('invoice_type', {
                    is: (val: string) => !isCompanyInvoice(val),
                    then: fields.firstname,
                }),
                lastname: yup.string().when('invoice_type', {
                    is: (val: string) => !isCompanyInvoice(val),
                    then: fields.lastname,
                }),
                company: yup
                    .string()
                    .nullable()
                    .when('invoice_type', {
                        is: (val: string) => isCompanyInvoice(val),
                        then: schema => schema.required('errors.string.company'),
                    }),
                vat_id: yup
                    .string()
                    .test('vat_id', 'errors.string.tin', (value: any) => {
                        if (value?.length > 0)
                            return value.length === 10 && /^[0-9]+$/.test(value)

                        return true
                    })
                    .nullable()
                    .when('invoice_type', {
                        is: (val: string) => isCompanyInvoice(val),
                        then: schema => schema.required('errors.string.tin'),
                    }),
            })
            .default(undefined),
        otherwise: schema => schema.nullable(),
    }),
    accept_regulations: yup
        .boolean()
        .test(
            'accept_regulations',
            'errors.checkbox.conditions',
            (value: any, context: any) => {
                if (context?.options?.context?.customer) return true

                return value
            }
        ),
})
